import React, { useState, useEffect, useRef } from 'react';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { CalendarToday } from '@material-ui/icons';

const useStyles = makeStyles(() => ({
  hideNativePicker: {
    '&::-webkit-calendar-picker-indicator': {
      display: 'none',
      WebkitAppearance: 'none',
    },
  },
}));

const EditableDateCell = ({
  value,
  onChange,
  disabled = false,
  align = 'center',
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const inputRef = useRef(null);

  const [localValue, setLocalValue] = useState(value ?? '');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) setLocalValue(value ?? '');
  }, [value, isEditing]);

  const commitIfChanged = (nextValue) => {
    const next = nextValue ?? '';
    const prev = value ?? '';
    if (onChange && next !== prev) onChange(next);
  };

  const handleFocus = () => {
    if (disabled || isEditing) return;
    setIsEditing(true);
  };

  const handleBlur = (e) => {
    // Don't blur if clicking on the calendar icon inside the same component
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsEditing(false);
    commitIfChanged(localValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setLocalValue(value ?? '');
      setIsEditing(false);
      setTimeout(() => inputRef.current?.blur(), 0);
    }
  };

  return (
    <div onBlur={handleBlur}>
      <TextField
        fullWidth
        value={localValue}
        disabled={disabled}
        type="date"
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onChange={(e) => setLocalValue(e.target.value)}
        InputProps={{
          disableUnderline: true,
          readOnly: disabled,
          inputRef,
          style: {
            color: isEditing && !disabled ? '#000' : theme.palette.grey[500],
          },
          endAdornment: !disabled ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                    try {
                      inputRef.current.showPicker();
                    } catch (_) {
                      // showPicker not supported
                    }
                  }
                }}
              >
                <CalendarToday
                  fontSize="small"
                  style={{ color: theme.palette.primary.main }}
                />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        inputProps={{
          className: classes.hideNativePicker,
          style: {
            textAlign: align,
            cursor: isEditing && !disabled ? 'text' : 'default',
          },
        }}
        style={{
          width: '100%',
          padding: 10,
        }}
      />
    </div>
  );
};

export default EditableDateCell;
